/* Catalog discovery uses real variant combinations and native Shopify sorting. */
(() => {
  const keys = { color: 'ona_color', size: 'ona_size', min: 'ona_min', max: 'ona_max' };
  const parse = card => { try { return JSON.parse(card.querySelector('[data-ona-media-data]').textContent); } catch { return null; } };
  const amount = value => value === '' ? null : Math.round(Number(value) * 100);
  const validPrice = value => value === '' || (/^(?:\d+(?:\.\d{1,2})?|\.\d{1,2})$/.test(value) && Number.isSafeInteger(Math.round(Number(value) * 100)));

  class OnaDiscovery extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.scope = this.dataset.onaScope;
      this.entries = [...this.querySelectorAll('ona-product-card')].map(card => ({ card, data: parse(card) })).filter(entry => entry.data);
      if (!this.entries.length || this.entries.length !== this.querySelectorAll('ona-product-card').length) return;
      this.filterControls = this.querySelector('[data-ona-filters]');
      this.sort = this.querySelector('[data-ona-sort]');
      this.priceDetails = this.querySelector('[data-ona-price-control]');
      this.minimum = this.querySelector('[data-ona-price-min]');
      this.maximum = this.querySelector('[data-ona-price-max]');
      this.priceError = this.querySelector('[data-ona-price-error]');
      this.count = this.querySelector('[data-ona-filter-count]');
      this.grid = this.querySelector('.ccf-product-grid');
      this.fullCatalog = this.dataset.onaComplete === 'true';
      this.locale = document.documentElement.lang || 'en';
      try { this.money = new Intl.NumberFormat(this.locale, { style: 'currency', currency: this.dataset.onaCurrency || 'USD' }); }
      catch { this.money = new Intl.NumberFormat(this.locale, { maximumFractionDigits: 2 }); }
      this.buildControls();
      this.state = this.readURL();

      this.addEventListener('change', event => {
        const filter = event.target.closest('[data-ona-filter]');
        if (filter) {
          this.state[filter.dataset.onaFilter] = filter.value;
          this.apply({ push: true });
        }
        if (event.target === this.sort) {
          event.stopPropagation();
          this.navigateSort();
        }
      }, { signal });
      this.addEventListener('submit', event => {
        if (event.target.matches('[data-ona-price-form]')) {
          event.preventDefault();
          this.applyPrice();
        } else if (event.target === this.sort?.form) {
          event.preventDefault();
          this.navigateSort();
        }
      }, { signal });
      this.addEventListener('click', event => {
        const remove = event.target.closest('[data-ona-remove-filter]');
        if (remove) {
          const name = remove.dataset.onaRemoveFilter;
          if (name === 'price') { this.state.min = ''; this.state.max = ''; }
          else this.state[name] = '';
          this.apply({ push: true });
          const remaining = this.querySelector('[data-ona-remove-filter]');
          if (remaining) remaining.focus(); else this.focusFilter();
        }
        if (event.target.closest('[data-ona-clear-filters]')) {
          this.state = { color: '', size: '', min: '', max: '' };
          this.apply({ push: true });
          this.focusFilter();
        }
        if (event.target.closest('ona-product-card')) requestAnimationFrame(() => this.saveSnapshot());
        if (event.target.closest('a[href]')) this.saveSnapshot();
      }, { signal });
      this.addEventListener('keydown', event => {
        if (event.key === 'Escape' && this.priceDetails?.open) {
          event.preventDefault();
          event.stopPropagation();
          this.closePrice(true);
        }
      }, { signal });
      document.addEventListener('pointerdown', event => {
        if (this.priceDetails?.open && !this.priceDetails.contains(event.target)) this.closePrice(false);
      }, { signal });
      document.addEventListener('focusin', event => {
        if (this.priceDetails?.open && !this.priceDetails.contains(event.target)) this.closePrice(false);
      }, { signal });
      this.priceDetails?.addEventListener('toggle', () => {
        if (!this.priceDetails.open) this.resetPriceDraft();
      }, { signal });
      window.addEventListener('popstate', () => { this.state = this.readURL(); this.apply({ restore: true }); this.restoreScroll(); }, { signal });
      window.addEventListener('hashchange', () => { this.state = this.readURL(); this.apply({ restore: true }); }, { signal });
      window.addEventListener('pagehide', () => this.saveSnapshot(), { signal });
      window.addEventListener('pageshow', event => {
        this.removeAttribute('aria-busy'); if (this.sort) this.sort.disabled = false;
        if (!event.persisted) return;
        this.state = this.readURL(); this.apply({ restore: true }); this.restoreScroll();
      }, { signal });

      this.apply({ restore: true });
      customElements.whenDefined('ona-product-card').then(() => {
        if (this.isConnected) this.apply({ restore: true });
      });
      if (performance.getEntriesByType('navigation')[0]?.type === 'back_forward') this.restoreScroll();
    }

    buildControls() {
      if (!this.fullCatalog || !this.filterControls || !this.entries.length) return;
      const values = { color: new Set(), size: new Set() };
      const prices = new Set();
      this.entries.forEach(({ data }) => data.variants.forEach(variant => {
        if (data.colorIndex >= 0) values.color.add(variant.options[data.colorIndex]);
        if (data.sizeIndex >= 0) values.size.add(variant.options[data.sizeIndex]);
        if (Number.isFinite(variant.amount)) prices.add(variant.amount);
      }));
      this.facetCounts = { color: values.color.size, size: values.size.size };
      Object.entries(values).forEach(([name, entries]) => {
        const select = this.querySelector(`[data-ona-filter="${name}"]`);
        [...entries].filter(Boolean).sort((a, b) => a.localeCompare(b, this.locale, { numeric: true })).forEach(value => {
          const option = document.createElement('option'); option.value = value; option.textContent = value; select.append(option);
        });
        this.querySelector(`[data-ona-${name}-control]`).hidden = entries.size < 2;
      });
      this.priceDetails.hidden = prices.size < 2;
      this.priceCount = prices.size;
      this.filterControls.hidden = values.color.size < 2 && values.size.size < 2 && prices.size < 2;
    }

    isActiveScope() {
      return this.dataset.onaRoute !== 'in_page' || location.hash === `#curtain-family-${this.scope}`;
    }

    readURL() {
      const query = new URL(location.href).searchParams;
      const state = { color: '', size: '', min: '', max: '' };
      if (!this.fullCatalog || !this.isActiveScope()) return state;
      Object.entries(keys).forEach(([key, param]) => { state[key] = query.get(param) || ''; });
      if (!validPrice(state.min)) state.min = '';
      if (!validPrice(state.max)) state.max = '';
      return state;
    }

    matches(data, variant) {
      const { color, size, min, max } = this.state;
      return (!color || (data.colorIndex >= 0 && variant.options[data.colorIndex] === color)) &&
        (!size || (data.sizeIndex >= 0 && variant.options[data.sizeIndex] === size)) &&
        (!min || (Number.isFinite(variant.amount) && variant.amount >= amount(min))) &&
        (!max || (Number.isFinite(variant.amount) && variant.amount <= amount(max)));
    }

    apply({ push = false, restore = false } = {}) {
      if (push) this.saveSnapshot();
      const snapshot = restore ? this.snapshot() : null;
      let count = 0;
      const filtered = Object.values(this.state).some(Boolean);
      this.entries.forEach(({ card, data }) => {
        const matches = data.variants.filter(variant => this.matches(data, variant));
        card.hidden = !matches.length;
        if (!matches.length) return;
        count++;
        const restored = snapshot?.selections?.[data.url];
        const current = restored || card.variant?.id || data.selected;
        const variant = matches.find(item => String(item.id) === String(current)) || matches.find(item => item.available) || matches[0];
        if ((filtered || restored) && variant && card.variant?.id !== variant.id) card.update?.(variant);
      });
      this.count.textContent = `${count} ${count === 1 ? 'style' : 'styles'}${this.fullCatalog ? '' : ' on this page'}`;
      this.querySelector('[data-ona-filter-empty]').hidden = count > 0;
      this.syncControls();
      this.renderChips();
      if (push) {
        const url = this.stateURL();
        if (url.href !== location.href) history.pushState(history.state, '', url);
        this.saveSnapshot();
      }
    }

    syncControls() {
      ['color', 'size'].forEach(name => {
        const select = this.querySelector(`[data-ona-filter="${name}"]`); if (!select) return;
        select.querySelectorAll('[data-ona-unknown]').forEach(option => option.remove());
        if (this.state[name] && ![...select.options].some(option => option.value === this.state[name])) {
          const option = document.createElement('option'); option.value = this.state[name]; option.textContent = this.state[name]; option.dataset.onaUnknown = ''; select.append(option);
          select.closest('label').hidden = false; this.filterControls.hidden = false;
        }
        select.value = this.state[name];
        select.closest('label').hidden = (this.facetCounts?.[name] || 0) < 2 && !this.state[name];
        select.closest('label').classList.toggle('is-active', Boolean(this.state[name]));
      });
      this.resetPriceDraft();
      if (this.priceDetails) {
        this.priceDetails.hidden = (this.priceCount || 0) < 2 && !this.state.min && !this.state.max;
        this.priceDetails.classList.toggle('is-active', Boolean(this.state.min || this.state.max));
        this.querySelector('[data-ona-price-dot]').hidden = !this.state.min && !this.state.max;
      }
      if (this.filterControls) this.filterControls.hidden = ![...this.filterControls.children].some(control => !control.hidden);
    }

    renderChips() {
      const holder = this.querySelector('[data-ona-filter-chips]'); holder.replaceChildren();
      const filters = [];
      if (this.state.color) filters.push(['color', `Color: ${this.state.color}`]);
      if (this.state.size) filters.push(['size', `Size: ${this.state.size}`]);
      if (this.state.min || this.state.max) {
        const min = this.state.min ? this.money.format(Number(this.state.min)) : '';
        const max = this.state.max ? this.money.format(Number(this.state.max)) : '';
        filters.push(['price', min && max ? `${min} – ${max}` : min ? `${min} and up` : `Up to ${max}`]);
      }
      filters.forEach(([name, label]) => {
        const button = document.createElement('button'); button.type = 'button'; button.dataset.onaRemoveFilter = name; button.setAttribute('aria-label', `Remove ${label}`);
        const text = document.createElement('span'); text.textContent = label;
        const cross = document.createElement('span'); cross.textContent = '×'; cross.setAttribute('aria-hidden', 'true');
        button.append(text, cross); holder.append(button);
      });
      this.querySelector('[data-ona-active-filters]').hidden = !filters.length;
    }

    applyPrice() {
      const min = this.minimum.value.trim(); const max = this.maximum.value.trim();
      const invalidMin = this.minimum.validity.badInput || !validPrice(min);
      const invalidMax = this.maximum.validity.badInput || !validPrice(max);
      const invalid = invalidMin || invalidMax;
      const reversed = min !== '' && max !== '' && Number(min) > Number(max);
      if (invalid || reversed) {
        this.priceError.hidden = false;
        this.priceError.textContent = reversed ? 'The maximum price must be at least the minimum.' : 'Enter a price of zero or more, with up to two decimal places.';
        this.minimum.setAttribute('aria-invalid', String(invalidMin));
        this.maximum.setAttribute('aria-invalid', String(invalidMax || reversed));
        (invalidMin ? this.minimum : this.maximum).focus();
        return;
      }
      this.state.min = min; this.state.max = max;
      this.apply({ push: true }); this.closePrice(true);
    }

    resetPriceDraft() {
      if (!this.minimum) return;
      this.minimum.value = this.state.min; this.maximum.value = this.state.max;
      this.minimum.removeAttribute('aria-invalid'); this.maximum.removeAttribute('aria-invalid');
      this.priceError.hidden = true; this.priceError.textContent = '';
    }

    closePrice(focus) {
      if (!this.priceDetails) return;
      this.priceDetails.open = false; this.resetPriceDraft();
      if (focus) this.priceDetails.querySelector('summary').focus();
    }

    focusFilter() {
      const visible = [...this.querySelectorAll('[data-ona-filter]')].find(select => !select.closest('label').hidden);
      (visible || (!this.priceDetails?.hidden && this.priceDetails?.querySelector('summary')) || this.sort)?.focus();
    }

    stateURL() {
      const url = new URL(location.href);
      Object.entries(keys).forEach(([key, param]) => this.state[key] ? url.searchParams.set(param, this.state[key]) : url.searchParams.delete(param));
      return url;
    }

    navigateSort() {
      if (!this.sort) return;
      this.saveSnapshot();
      const url = this.stateURL();
      url.pathname = new URL(this.sort.form.action, location.href).pathname;
      url.searchParams.set('sort_by', this.sort.value); url.searchParams.delete('page'); url.hash = '';
      if (url.href === location.href) return;
      this.setAttribute('aria-busy', 'true'); this.sort.disabled = true;
      location.assign(url.href);
    }

    snapshot() { return history.state?.onaDiscovery?.[this.scope]; }

    saveSnapshot() {
      if (!this.isConnected || !this.isActiveScope()) return;
      const selections = {};
      this.entries.forEach(({ card, data }) => { selections[data.url] = card.variant?.id || data.selected; });
      const state = history.state || {};
      try { history.replaceState({ ...state, onaDiscovery: { ...state.onaDiscovery, [this.scope]: { selections, scroll: window.scrollY } } }, ''); } catch { /* Browsing remains usable if history is unavailable. */ }
    }

    restoreScroll() {
      const saved = this.snapshot(); if (!saved || !this.isActiveScope()) return;
      requestAnimationFrame(() => { if (this.isConnected) window.scrollTo({ top: saved.scroll || 0, behavior: 'instant' }); });
    }

    disconnectedCallback() { this.controller?.abort(); this.controller = null; }
  }
  if (!customElements.get('ona-discovery')) customElements.define('ona-discovery', OnaDiscovery);
})();
